import { useCallback, useEffect, useState } from "react";
import { api, mesajEroare } from "./api.js";
import {
  ACTIUNI_DEFECT, CATEGORII_INTERVENTIE, CEASURI_SLA, CRITICITATI,
  MODURI_ACOPERIRE, MOTIVE_BLOCARE, SEVERITATI, STARI_LA_PLECARE, STARI_SISTEM,
  STARI_TICHET, STARI_VIZITA, TIPURI_GARANTIE, eticheta,
} from "./enums.js";
import { data, dataOra } from "./text.js";
import { um } from "./um.js";
import { Contor } from "./ContorAbonament.jsx";
import { tr } from "./i18n.js";

const INTERN = [
  "ADMIN_TENANT", "MANAGER_TEHNIC", "DISPECER", "COORDONATOR",
  "TEHNICIAN", "MAGAZIONER", "FINANCIAR", "AUDITOR",
];
const eClient = (rol) => String(rol).startsWith("CLIENT_");

const lei = (v) =>
  v === null || v === undefined ? "—"
    : Number(v).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " lei";
/** „0h 27m" nu se citește ca 27 de minute — ora se scrie doar când există. */
const LUNI = ["ianuarie", "februarie", "martie", "aprilie", "mai", "iunie",
  "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie"];
/** „2026-08" nu e o lună pentru nimeni. */
const luna = (v) => {
  const [an, l] = String(v).split("-");
  return LUNI[Number(l) - 1] ? `${tr(LUNI[Number(l) - 1])} ${an}` : v;
};

const durata = (m) => {
  if (!m) return "—";
  const o = Math.floor(m / 60); const min = m % 60;
  if (!o) return `${min}m`;
  return min ? `${o}h ${min}m` : `${o}h`;
};

/** Cât mai e până la termen, citit ca un om — și cât de tare trebuie să sară în ochi. */
function ceasCitit(minute) {
  if (minute === null || minute === undefined) return { text: "—", clasa: "" };
  if (minute < 0) return { text: tr("depășit cu {0}", durata(-minute)), clasa: "vechime-90" };
  if (minute < 120) return { text: tr("{0} rămase", durata(minute)), clasa: "vechime-60" };
  if (minute < 8 * 60) return { text: tr("{0} rămase", durata(minute)), clasa: "vechime-30" };
  return { text: tr("{0} rămase", durata(minute)), clasa: "" };
}

/**
 * MT15 — portalul clientului.
 *
 * Un singur ecran, cu subtaburi, nu o a doua aplicație: e aceeași platformă,
 * văzută dinspre client. Conturile firmei îl pot deschide și ele, dar trebuie
 * să aleagă întâi clientul — un dispecer care are omul la telefon vede exact
 * ecranul pe care i-l descrie, nu unul asemănător.
 */
export default function Portal({ eu }) {
  const [sub, setSub] = useState("rezumat");
  const firma = INTERN.includes(eu.rol);
  const [clienti, setClienti] = useState([]);
  const [client, setClient] = useState(eClient(eu.rol) ? eu.clientId : "");

  useEffect(() => {
    if (!firma) return;
    api.clienti().then((l) => {
      const lista = Array.isArray(l) ? l : l?.linii ?? [];
      setClienti(lista);
      setClient((c) => c || lista[0]?.id || "");
    }).catch(() => setClienti([]));
  }, [firma]);

  const gata = eClient(eu.rol) || !!client;

  return (
    <div className="continut">
      <h2>{tr("Portalul clientului")}</h2>
      <p className="subtitlu">
        {eClient(eu.rol)
          ? tr("Sistemele tale, intervențiile executate la tine, documentele și rapoartele tale.")
          : tr("Exact ce vede clientul când se autentifică. Alege clientul pentru care deschizi portalul.")}
      </p>

      {firma && (
        <div className="linie-actiune">
          <label className="camp">
            {tr("Client")}
            <select value={client} onChange={(e) => setClient(e.target.value)}>
              <option value="">{tr("— alege un client —")}</option>
              {clienti.map((c) => <option key={c.id} value={c.id}>{tr(c.denumire)}</option>)}
            </select>
          </label>
        </div>
      )}

      <div className="taburi-sisteme">
        {[
          ["rezumat", "Rezumat"],
          ["sisteme", "Sistemele mele"],
          ["interventii", "Procese-verbale"],
          ["documente", "Documente"],
          ["rapoarte", "Rapoarte"],
        ].map(([cod, titlu]) => (
          <button key={cod} className={`tab-sistem ${sub === cod ? "activ" : ""}`}
            onClick={() => setSub(cod)}>{titlu}</button>
        ))}
      </div>

      {!gata && <p className="gol">{tr("Alege un client ca să deschizi portalul.")}</p>}
      {gata && sub === "rezumat" && <Rezumat client={firma ? client : null} />}
      {gata && sub === "sisteme" && <Sisteme client={firma ? client : null} />}
      {gata && sub === "interventii" && <Interventii client={firma ? client : null} />}
      {gata && sub === "documente" && <Documente client={firma ? client : null} />}
      {gata && sub === "rapoarte" && <Rapoarte client={firma ? client : null} />}
    </div>
  );
}

/** Încărcare + eroare, o singură dată, ca fiecare panou să nu le rescrie. */
function useIncarca(fn, deps) {
  const [date, setDate] = useState(null);
  const [eroare, setEroare] = useState("");
  const [incarca, setIncarca] = useState(true);
  // Dependențele vin de la apelant (clientul ales, id-ul deschis): funcția
  // însăși se rescrie la fiecare randare, deci n-are ce căuta în listă.
  const rulează = useCallback(() => {
    setIncarca(true); setEroare("");
    fn().then(setDate).catch((e) => setEroare(mesajEroare(e))).finally(() => setIncarca(false));
  }, deps);
  useEffect(() => { rulează(); }, [rulează]);
  return { date, eroare, incarca, reia: rulează };
}

// ── Rezumat ───────────────────────────────────────────────────────────────

function Rezumat({ client }) {
  const { date, eroare, incarca } = useIncarca(() => api.portalRezumat(client), [client]);
  if (incarca) return <p className="gol">{tr("Se încarcă…")}</p>;
  if (eroare) return <p className="eroare">{eroare}</p>;
  if (!date) return null;

  const {
    tichete = [], urmatoareleVizite = [], garantiiCareExpira = [],
    blocaje = [], ultimeleInterventii = [], pachete = [],
  } = date;

  return (
    <>
      {blocaje.length > 0 && (
        <div className="panou panou-atentie">
          <h3>{tr("Așteaptă un răspuns de la tine")}</h3>
          <p className="subtitlu">
            {tr("Lucrări care stau pe loc din motive care țin de partea ta. Cât timp rămân aici, nu pot fi duse mai departe.")}
          </p>
          <div className="panou-corp">
            <div className="tabel-derulabil">
              <table className="tabel">
                <thead><tr><th>{tr("Ce e blocat")}</th><th>{tr("Unde")}</th><th>{tr("De ce")}</th><th>{tr("Din")}</th></tr></thead>
                <tbody>
                  {blocaje.map((b) => (
                    <tr key={b.id}>
                      <td>{tr(b.descriere)}</td>
                      <td>{tr(b.locatie?.denumire) ?? "—"}{b.sistemInstalat ? ` · ${b.sistemInstalat.denumire}` : ""}</td>
                      <td>{eticheta(MOTIVE_BLOCARE, b.motivBlocare)}</td>
                      <td>{data(b.deschisaLa)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="panou">
        <h3>{tr("Tichetele mele deschise")}</h3>
        <div className="panou-corp">
          {tichete.length === 0 && <p className="gol">{tr("Niciun tichet deschis.")}</p>}
          {tichete.length > 0 && (
            <div className="tabel-derulabil">
              <table className="tabel">
                <thead>
                  <tr><th>{tr("Număr")}</th><th>{tr("Titlu")}</th><th>{tr("Unde")}</th><th>{tr("Stare")}</th>
                    <th>{tr("Severitate")}</th><th>{tr("Termen")}</th></tr>
                </thead>
                <tbody>
                  {tichete.map((t) => {
                    const c = ceasCitit(t.ceas?.minuteRamase);
                    return (
                      <tr key={t.id}>
                        <td className="cod-mic">{t.numar}</td>
                        <td>{tr(t.titlu)}</td>
                        <td>{tr(t.locatie?.denumire) ?? "—"}</td>
                        <td>{eticheta(STARI_TICHET, t.status)}</td>
                        <td>{eticheta(SEVERITATI, t.severitate)}</td>
                        <td className={c.clasa}>
                          {t.ceas ? `${eticheta(CEASURI_SLA, t.ceas.tipCeas)}: ${c.text}` : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="panou">
        <h3>{tr("Ce urmează la mine")}</h3>
        <div className="panou-corp">
          {urmatoareleVizite.length === 0 && <p className="gol">{tr("Nicio vizită programată.")}</p>}
          {urmatoareleVizite.length > 0 && (
            <div className="tabel-derulabil">
              <table className="tabel">
                <thead><tr><th>{tr("Număr")}</th><th>{tr("Când")}</th><th>{tr("Unde")}</th><th>{tr("Ce")}</th><th>{tr("Stare")}</th></tr></thead>
                <tbody>
                  {urmatoareleVizite.map((v) => (
                    <tr key={v.id}>
                      <td className="cod-mic">{v.numar}</td>
                      <td>{data(v.dataProgramata)}{v.oraProgramata ? ` · ${v.oraProgramata}` : ""}</td>
                      <td>{tr(v.locatie?.denumire) ?? "—"}</td>
                      <td>{eticheta(CATEGORII_INTERVENTIE, v.categorie)} — {v.scopInterventie}</td>
                      <td>{eticheta(STARI_VIZITA, v.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {pachete.length > 0 && (
        <div className="panou">
          <h3>{tr("Din abonament")}</h3>
          <p className="subtitlu">{tr("Cât include contractul și cât s-a consumat.")}</p>
          <div className="panou-corp">
            {pachete.map((p) => <Contor key={p.id} contor={p} />)}
          </div>
        </div>
      )}

      <div className="panou">
        <h3>{tr("Ce expiră curând din garanție")}</h3>
        <div className="panou-corp">
          {garantiiCareExpira.length === 0 && <p className="gol">{tr("Nicio garanție care expiră în următoarele 60 de zile.")}</p>}
          {garantiiCareExpira.length > 0 && (
            <div className="tabel-derulabil">
              <table className="tabel">
                <thead><tr><th>{tr("Echipament")}</th><th>{tr("Unde")}</th><th>{tr("Tip")}</th><th>{tr("Expiră")}</th></tr></thead>
                <tbody>
                  {garantiiCareExpira.map((g) => (
                    <tr key={g.id}>
                      <td>
                        {g.echipament?.eticheta ?? g.echipament?.tipComponenta?.denumire ?? "—"}
                        {g.echipament?.serie ? <span className="cod-mic"> · {g.echipament.serie}</span> : null}
                      </td>
                      <td>{tr(g.echipament?.locatie?.denumire) ?? "—"}</td>
                      <td>{eticheta(TIPURI_GARANTIE, g.tip)}</td>
                      <td>{data(g.expiraLa)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="panou">
        <h3>{tr("Ultimele intervenții")}</h3>
        <div className="panou-corp">
          {ultimeleInterventii.length === 0 && <p className="gol">{tr("Nicio intervenție încheiată încă.")}</p>}
          {ultimeleInterventii.length > 0 && (
            <div className="tabel-derulabil">
              <table className="tabel">
                <thead><tr><th>{tr("Fișă")}</th><th>{tr("Când")}</th><th>{tr("Unde")}</th><th>{tr("Ce")}</th></tr></thead>
                <tbody>
                  {ultimeleInterventii.map((f) => (
                    <tr key={f.id}>
                      <td className="cod-mic">{f.numar}</td>
                      <td>{data(f.data)}</td>
                      <td>{tr(f.vizita?.locatie?.denumire) ?? "—"}</td>
                      <td>{eticheta(CATEGORII_INTERVENTIE, f.vizita?.categorie)}
                        {f.scopInterventie ? ` — ${f.scopInterventie}` : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Sistemele instalate ───────────────────────────────────────────────────

function Sisteme({ client }) {
  const { date, eroare, incarca } = useIncarca(() => api.portalSisteme(client), [client]);
  const [deschis, setDeschis] = useState({});
  if (incarca) return <p className="gol">{tr("Se încarcă…")}</p>;
  if (eroare) return <p className="eroare">{eroare}</p>;
  const locatii = date ?? [];
  if (!locatii.length) return <p className="gol">{tr("Niciun sistem înregistrat încă.")}</p>;

  return (
    <>
      {locatii.map((l) => (
        <div key={l.id} className="panou">
          <h3>{tr(l.denumire)}</h3>
          <p className="subtitlu">
            {[l.adresa, l.oras, l.judet].filter(Boolean).join(", ") || tr("fără adresă")}
          </p>
          <div className="panou-corp">
            {(l.sistemeInstalate ?? []).length === 0 && <p className="gol">{tr("Niciun sistem la această locație.")}</p>}
            {(l.sistemeInstalate ?? []).map((s) => {
              const cheie = s.id;
              const desfacut = !!deschis[cheie];
              return (
                <div key={s.id} className="portal-sistem">
                  <div className="portal-sistem-antet">
                    <div>
                      <strong>{tr(s.denumire)}</strong>{" "}
                      <span className="cod-mic">{s.tipSistem?.cod}</span>
                      <div className="subtitlu">
                        {eticheta(STARI_SISTEM, s.stare)} {tr("· criticitate")} {eticheta(CRITICITATI, s.criticitate)}
                        {" · "}{tr("ultima mentenanță")} {data(s.ultimaMentenanta)}
                        {" · "}{tr("următoarea")} {data(s.urmatoareaMentenanta)}
                      </div>
                    </div>
                    <button className="buton-mic"
                      onClick={() => setDeschis((d) => ({ ...d, [cheie]: !desfacut }))}>
                      {desfacut ? tr("ascunde echipamentele") : tr("vezi cele {0} echipamente", (s.echipamente ?? []).length)}
                    </button>
                  </div>
                  {desfacut && (
                    <div className="tabel-derulabil">
                      <table className="tabel">
                        <thead>
                          <tr><th>{tr("Echipament")}</th><th>{tr("Serie")}</th><th>{tr("Poziție")}</th><th>{tr("Stare")}</th>
                            <th>{tr("Garanție")}</th><th>{tr("Ultima mentenanță")}</th></tr>
                        </thead>
                        <tbody>
                          {(s.echipamente ?? []).map((e) => (
                            <tr key={e.id}>
                              <td>{e.eticheta ?? e.tipComponenta?.denumire ?? "—"}
                                <div className="subtitlu">{tr(e.subsistem?.denumire)}</div></td>
                              <td className="cod-mic">{e.serie ?? "—"}</td>
                              <td>{e.pozitieFizica ?? "—"}</td>
                              <td>{eticheta(STARI_SISTEM, e.stare)}</td>
                              <td className={e.garantieActiva && e.zileRamaseGarantie <= 30 ? "vechime-60" : ""}>
                                {e.garantieExpiraLa
                                  ? (e.garantieActiva
                                    ? tr("până la {0} ({1} zile)", data(e.garantieExpiraLa), e.zileRamaseGarantie)
                                    : tr("expirată la {0}", data(e.garantieExpiraLa)))
                                  : "—"}
                              </td>
                              <td>{data(e.ultimaMentenanta)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
}

// ── Procesele-verbale ─────────────────────────────────────────────────────

function Interventii({ client }) {
  const { date, eroare, incarca } = useIncarca(() => api.portalInterventii(client), [client]);
  const [ales, setAles] = useState(null);
  if (incarca) return <p className="gol">{tr("Se încarcă…")}</p>;
  if (eroare) return <p className="eroare">{eroare}</p>;
  const fise = date ?? [];

  return (
    <div className="panou">
      <h3>{tr("Intervențiile executate la mine")}</h3>
      <p className="subtitlu">
        {tr("Procesul-verbal al fiecărei lucrări încheiate: ce s-a constatat, ce s-a remediat, ce piese s-au folosit și cât a costat.")}
      </p>
      <div className="panou-corp">
        {fise.length === 0 && <p className="gol">{tr("Nicio intervenție încheiată încă.")}</p>}
        {fise.length > 0 && (
          <div className="tabel-derulabil">
            <table className="tabel">
              <thead>
                <tr><th>{tr("Fișă")}</th><th>{tr("Când")}</th><th>{tr("Unde")}</th><th>{tr("Ce")}</th><th>{tr("Durată")}</th>
                  <th>{tr("Tehnician")}</th><th>{tr("Stare")}</th><th /></tr>
              </thead>
              <tbody>
                {fise.map((f) => (
                  <tr key={f.id}>
                    <td className="cod-mic">{f.numar}</td>
                    <td>{data(f.data)}</td>
                    <td>{tr(f.vizita?.locatie?.denumire) ?? "—"}</td>
                    <td>{eticheta(CATEGORII_INTERVENTIE, f.vizita?.categorie)}
                      {f.scopInterventie ? ` — ${f.scopInterventie}` : ""}</td>
                    <td>{durata(f.durataMinute)}</td>
                    <td>{f.vizita?.tehnicianPrincipal?.numeComplet ?? "—"}</td>
                    <td>{eticheta(STARI_VIZITA, f.status)}
                      {f.necesitaRevenire && <span className="marcaj"> {tr("necesită revenire")}</span>}</td>
                    <td><button className="buton-mic" onClick={() => setAles(f.id)}>{tr("deschide")}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {ales && <ProcesVerbal id={ales} client={client} inchide={() => setAles(null)} />}
    </div>
  );
}

function ProcesVerbal({ id, client, inchide }) {
  const { date, eroare, incarca } = useIncarca(() => api.portalInterventie(id, client), [id, client]);
  return (
    <div className="modal-fundal" onClick={inchide}>
      <div className="modal modal-lat" onClick={(e) => e.stopPropagation()}>
        {incarca && <p className="gol">{tr("Se încarcă…")}</p>}
        {eroare && <p className="eroare">{eroare}</p>}
        {date && (
          <>
            <h3>{tr("Proces-verbal")} {date.numar}</h3>
            <p className="subtitlu">
              {data(date.data)} · {tr(date.vizita?.locatie?.denumire)}
              {" · "}{eticheta(CATEGORII_INTERVENTIE, date.vizita?.categorie)}
              {" · "}{tr("tehnician")} {date.vizita?.tehnicianPrincipal?.numeComplet ?? "—"}
            </p>

            <dl className="explica-drepturi">
              <div><dt>{tr("Sosire")}</dt><dd>{dataOra(date.oraSosire)}</dd></div>
              <div><dt>{tr("Durată lucrare")}</dt><dd>{durata(date.durataMinute)}</dd></div>
              <div><dt>{tr("Deplasare")}</dt><dd>{durata(date.durataDeplasareMinute)}</dd></div>
              <div><dt>{tr("Stare la plecare")}</dt>
                <dd>{eticheta(STARI_LA_PLECARE, date.stareLaPlecare)}</dd></div>
            </dl>

            {date.descriereConstatari && (
              <><h4>{tr("Ce s-a constatat")}</h4><p>{date.descriereConstatari}</p></>
            )}
            {date.remedieriEfectuate && (
              <><h4>{tr("Ce s-a remediat")}</h4><p>{date.remedieriEfectuate}</p></>
            )}
            {date.cauzaRadacina && (
              <><h4>{tr("Cauza")}</h4><p>{date.cauzaRadacina}</p></>
            )}
            {date.motivStornare && (
              <div className="panou-atentie">
                <p><strong>{tr("Fișă stornată.")}</strong> {date.motivStornare}</p>
              </div>
            )}

            {(date.operatii ?? []).length > 0 && (
              <>
                <h4>{tr("Ce s-a lucrat")}</h4>
                <div className="tabel-derulabil">
                  <table className="tabel">
                    <thead><tr><th>{tr("Operație")}</th><th>{tr("Cantitate")}</th><th>{tr("Timp")}</th><th>{tr("Acoperire")}</th></tr></thead>
                    <tbody>
                      {date.operatii.map((o, i) => (
                        <tr key={i}>
                          <td><span className="cod-mic cod-inainte">{o.operatie?.cod}</span>{tr(o.operatie?.denumire)}
                            {o.observatie ? <div className="subtitlu">{o.observatie}</div> : null}</td>
                          <td>{Number(o.cantitate)}</td>
                          <td>{durata(o.timpRealMinute)}</td>
                          <td>{eticheta(MODURI_ACOPERIRE, o.modAcoperire)}
                            {o.motivAcoperire ? <div className="subtitlu">{o.motivAcoperire}</div> : null}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {(date.articole ?? []).length > 0 && (
              <>
                <h4>{tr("Piese și materiale")}</h4>
                <div className="tabel-derulabil">
                  <table className="tabel">
                    <thead><tr><th>{tr("Articol")}</th><th>{tr("Cantitate")}</th><th>{tr("Serie")}</th><th>{tr("Acoperire")}</th></tr></thead>
                    <tbody>
                      {date.articole.map((a, i) => (
                        <tr key={i}>
                          <td><span className="cod-mic cod-inainte">{a.articol?.cod}</span>{tr(a.articol?.denumire)}</td>
                          <td>{Number(a.cantitate)} {um(a.articol?.unitateMasura)}</td>
                          <td className="cod-mic">{a.serie ?? "—"}</td>
                          <td>{eticheta(MODURI_ACOPERIRE, a.modAcoperire)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {(date.defecte ?? []).length > 0 && (
              <>
                <h4>{tr("Defecte constatate")}</h4>
                <ul>
                  {date.defecte.map((d, i) => (
                    <li key={i}>
                      {d.echipament?.eticheta ?? d.tipComponenta?.denumire ?? tr("echipament")}
                      {d.echipament?.serie ? ` (${d.echipament.serie})` : ""} — {d.descriereDefect}
                      {" · "}{eticheta(ACTIUNI_DEFECT, d.actiune)}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {date.valoare && (
              <>
                <h4>{tr("Valoarea lucrării")}</h4>
                <div className="tabel-derulabil">
                  <table className="tabel">
                    <tbody>
                      <tr><td>{tr("Manoperă")}</td><td>{lei(date.valoare.manopera)}</td></tr>
                      <tr><td>{tr("Materiale")}</td><td>{lei(date.valoare.materiale)}</td></tr>
                      <tr><td><strong>{tr("Total")}</strong></td><td><strong>{lei(date.valoare.total)}</strong></td></tr>
                    </tbody>
                  </table>
                </div>
                <p className="subtitlu">
                  {tr("Sumele sunt cele înghețate la validarea fișei — nu se schimbă dacă lista de prețuri se schimbă mai târziu.")}
                </p>
              </>
            )}
            {date.valoare === null && (
              <p className="subtitlu">
                {tr("Funcția ta vede lucrarea, nu devizul ei.")}
              </p>
            )}

            {date.dataSemnare && (
              <p className="subtitlu">
                {tr("Semnat de")} {date.numeSemnatarClient} {tr("la")} {dataOra(date.dataSemnare)}.
              </p>
            )}

            <div className="modal-actiuni">
              <button className="buton" onClick={inchide}>{tr("închide")}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Documente ─────────────────────────────────────────────────────────────

function Documente({ client }) {
  const { date, eroare, incarca } = useIncarca(() => api.portalDocumente(client), [client]);
  if (incarca) return <p className="gol">{tr("Se încarcă…")}</p>;
  if (eroare) return <p className="eroare">{eroare}</p>;
  const docs = date ?? [];
  return (
    <div className="panou">
      <h3>{tr("Documentele mele")}</h3>
      <p className="subtitlu">
        {tr("Doar documentele pe care firma de service le-a marcat explicit ca fiind pentru tine. Restul dosarului tehnic rămâne intern.")}
      </p>
      <div className="panou-corp">
        {docs.length === 0 && (
          <p className="gol">{tr("Niciun document pus la dispoziție încă.")}</p>
        )}
        {docs.length > 0 && (
          <div className="tabel-derulabil">
            <table className="tabel">
              <thead>
                <tr><th>{tr("Document")}</th><th>{tr("Rubrică")}</th><th>{tr("Unde")}</th><th>{tr("Versiune")}</th>
                  <th>{tr("Valabil de la")}</th><th /></tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id}>
                    <td>{tr(d.denumire)}
                      {d.hashSHA256 && (
                        <div className="subtitlu cod-mic" title={tr("amprenta fișierului")}>
                          {d.hashSHA256.slice(0, 16)}…
                        </div>
                      )}</td>
                    <td>{tr(d.categorie?.denumire) ?? "—"}</td>
                    <td>{d.sistemInstalat?.denumire ?? d.locatie?.denumire ?? "—"}</td>
                    <td>{tr("v")}{d.versiune}</td>
                    <td>{data(d.valabilDeLa ?? d.incarcatLa)}</td>
                    <td>
                      <button className="buton-mic"
                        onClick={() => api.descarcaDocument(d.id, d.denumire)}>{tr("descarcă")}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Rapoarte ──────────────────────────────────────────────────────────────

function Rapoarte({ client }) {
  const sla = useIncarca(() => api.portalSla(client), [client]);
  const chelt = useIncarca(() => api.portalCheltuieli(client).catch(() => null), [client]);
  const echip = useIncarca(() => api.portalEchipamente(client), [client]);

  return (
    <>
      <div className="panou">
        <h3>{tr("Conformitatea cu termenele promise")}</h3>
        <p className="subtitlu">
          {tr("Câte tichete s-au încadrat în termenul din contract și câte nu. Se calculează din ceasurile reale, nu dintr-o stare salvată.")}
        </p>
        <div className="panou-corp">
          {sla.incarca && <p className="gol">{tr("Se încarcă…")}</p>}
          {sla.eroare && <p className="eroare">{sla.eroare}</p>}
          {sla.date && (sla.date.peTipDeCeas ?? []).length === 0 && (
            <p className="gol">{tr("Niciun tichet cu termen în perioada asta.")}</p>
          )}
          {sla.date && (sla.date.peTipDeCeas ?? []).length > 0 && (
            <>
              <div className="tabel-derulabil">
                <table className="tabel">
                  <thead><tr><th>{tr("Ceas")}</th><th>{tr("Total")}</th><th>{tr("În termen")}</th><th>{tr("Depășite")}</th><th>{tr("Procent")}</th></tr></thead>
                  <tbody>
                    {sla.date.peTipDeCeas.map((r) => (
                      <tr key={r.tipCeas}>
                        <td>{eticheta(CEASURI_SLA, r.tipCeas)}</td>
                        <td>{r.total}</td>
                        <td>{r.inTermen}</td>
                        <td className={r.depasite > 0 ? "vechime-60" : ""}>{r.depasite}</td>
                        <td>{r.procentInTermen === null ? "—" : `${r.procentInTermen}%`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(sla.date.depasiri ?? []).length > 0 && (
                <>
                  <h4>{tr("Unde s-a depășit")}</h4>
                  <div className="tabel-derulabil">
                    <table className="tabel">
                      <thead><tr><th>{tr("Tichet")}</th><th>{tr("Ceas")}</th><th>{tr("Termen")}</th><th>{tr("Întârziere")}</th></tr></thead>
                      <tbody>
                        {sla.date.depasiri.map((d, i) => (
                          <tr key={i}>
                            <td><span className="cod-mic cod-inainte">{d.tichet?.numar}</span>{tr(d.tichet?.titlu)}</td>
                            <td>{eticheta(CEASURI_SLA, d.tipCeas)}</td>
                            <td>{dataOra(d.termenLa)}</td>
                            <td className="vechime-60">{durata(d.minuteIntarziere)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="panou">
        <h3>{tr("Istoricul cheltuielilor")}</h3>
        <div className="panou-corp">
          {chelt.incarca && <p className="gol">{tr("Se încarcă…")}</p>}
          {chelt.date === null && !chelt.incarca && (
            <p className="subtitlu">{tr("Funcția ta vede lucrările, nu devizele lor.")}</p>
          )}
          {chelt.date && (chelt.date.peLuna ?? []).length === 0 && (
            <p className="gol">{tr("Nicio lucrare facturabilă încheiată încă.")}</p>
          )}
          {chelt.date && (chelt.date.peLuna ?? []).length > 0 && (
            <>
              <div className="tabel-derulabil">
                <table className="tabel">
                  <thead><tr><th>{tr("Luna")}</th><th>{tr("Intervenții")}</th><th>{tr("Manoperă")}</th><th>{tr("Materiale")}</th><th>{tr("Total")}</th></tr></thead>
                  <tbody>
                    {chelt.date.peLuna.map((l) => (
                      <tr key={l.luna}>
                        <td>{luna(l.luna)}</td><td>{l.interventii}</td>
                        <td>{lei(l.manopera)}</td><td>{lei(l.materiale)}</td>
                        <td><strong>{lei(l.total)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(chelt.date.peCategorie ?? []).length > 0 && (
                <>
                  <h4>{tr("Pe tip de intervenție")}</h4>
                  <div className="tabel-derulabil">
                    <table className="tabel">
                      <tbody>
                        {chelt.date.peCategorie.map((c) => (
                          <tr key={c.categorie}>
                            <td>{eticheta(CATEGORII_INTERVENTIE, c.categorie)}</td>
                            <td>{lei(c.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="panou">
        <h3>{tr("Ce îmi dă cel mai des de lucru")}</h3>
        <p className="subtitlu">
          {tr("Numărat din defectele constatate de tehnician pe teren, nu din tichetele deschise: primul spune ce s-a găsit, al doilea ce s-a reclamat.")}
        </p>
        <div className="panou-corp">
          {echip.incarca && <p className="gol">{tr("Se încarcă…")}</p>}
          {echip.eroare && <p className="eroare">{echip.eroare}</p>}
          {echip.date && echip.date.length === 0 && (
            <p className="gol">{tr("Niciun defect consemnat pe echipamente.")}</p>
          )}
          {echip.date && echip.date.length > 0 && (
            <div className="tabel-derulabil">
              <table className="tabel">
                <thead>
                  <tr><th>{tr("Echipament")}</th><th>{tr("Unde")}</th><th>{tr("Sistem")}</th><th>{tr("Defecte")}</th>
                    <th>{tr("Înlocuiri")}</th><th>{tr("Ultimul defect")}</th><th>{tr("Stare")}</th></tr>
                </thead>
                <tbody>
                  {echip.date.map((r) => (
                    <tr key={r.echipament.id}>
                      <td>{r.echipament.eticheta ?? r.echipament.tipComponenta?.denumire ?? "—"}
                        {r.echipament.serie ? <div className="subtitlu cod-mic">{r.echipament.serie}</div> : null}</td>
                      <td>{tr(r.echipament.locatie?.denumire) ?? "—"}</td>
                      <td>{tr(r.echipament.sistemInstalat?.denumire) ?? "—"}</td>
                      <td>{r.defecte}</td>
                      <td>{r.inlocuiri}</td>
                      <td>{data(r.ultimulDefect)}</td>
                      <td>{eticheta(STARI_SISTEM, r.echipament.stare)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
